import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { Button, Spinner, ui } from '@/components/ui';
import { useSession, useSignOut } from '@/features/auth/session';
import { useBusiness } from '@/features/business/BusinessProvider';
import { useStaffAccess } from '@/features/platform-analytics/AnalyticsGate';
import { EnforcementGuard } from '@/features/enforcement/EnforcementGuard';
import { useTenantKey } from '@/features/business/useTenantKey';
import { P, useCan, type Permission } from '@/permissions';
import { notificationsService } from '@/services/api';
import s from './Layout.module.css';

interface NavItem {
  to: string;
  label: string;
  permission?: Permission;
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');

const NAV: { group: string; items: NavItem[] }[] = [
  {
    group: 'Reviews',
    items: [
      { to: '/', label: 'Overview' },
      { to: '/reviews', label: 'Reviews' },
      { to: '/invitations', label: 'Invitations', permission: P.InvitationsRead },
      { to: '/analytics', label: 'Analytics', permission: P.AnalyticsRead },
    ],
  },
  {
    group: 'Data',
    items: [
      { to: '/customers', label: 'Customers', permission: P.CustomersRead },
      { to: '/products', label: 'Products' },
      { to: '/locations', label: 'Locations' },
    ],
  },
  {
    group: 'Grow',
    items: [
      { to: '/widgets', label: 'Widgets' },
      { to: '/integrations', label: 'API & webhooks', permission: P.ApiKeysManage },
    ],
  },
  {
    group: 'Account',
    items: [
      { to: '/team', label: 'Team' },
      { to: '/billing', label: 'Billing', permission: P.BillingRead },
      { to: '/settings', label: 'Business profile' },
      { to: '/help', label: 'Help' },
    ],
  },
];

export function DashboardLayout() {
  const { data: session } = useSession();
  const { businesses, selectedId, select, context, loading } = useBusiness();
  const can = useCan();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const key = useTenantKey();
  const [menuOpenOn, setMenuOpenOn] = useState<string | null>(null);
  const { pathname } = useLocation();
  const unread = useQuery({
    queryKey: key('notifications', 'unread'),
    queryFn: notificationsService.unread,
    refetchInterval: 60_000,
    enabled: Boolean(context),
  });

  const staffAccess = useStaffAccess();

  if (loading) return <Spinner />;
  const isStaff = session?.user.platformRole !== undefined && session.user.platformRole !== 'NONE';

  return (
    <div className={s.shell}>
      <aside
        className={`${s.side} ${menuOpenOn === pathname ? s.sideOpen : ''}`}
        aria-label="Dashboard"
      >
        <Link to="/" className={s.brand}>
          <img src="/brand/fairvero-logo.png" alt="Fairvero" width={126} height={32} />
          <small>Business</small>
        </Link>
        {NAV.map((g) => {
          const items = g.items.filter((i) => !i.permission || can(i.permission));
          if (!items.length) return null;
          return (
            <nav key={g.group} className={s.nav} aria-label={g.group}>
              <span className={s.navGroup}>{g.group}</span>
              {items.map((i) => (
                <NavLink key={i.to} to={i.to} end={i.to === '/'}>
                  {i.label}
                </NavLink>
              ))}
            </nav>
          );
        })}
        {isStaff ? (
          <nav className={s.nav} aria-label="Platform">
            <span className={s.navGroup}>Platform</span>
            {staffAccess.canSeeAnalytics ? (
              <>
                <NavLink to="/admin/analytics">Analytics</NavLink>
                <NavLink to="/admin/businesses">Businesses</NavLink>
              </>
            ) : null}
            {staffAccess.canEnforce ? (
              <NavLink to="/admin/enforcement">Trust &amp; Safety</NavLink>
            ) : null}
            {staffAccess.canGrantPlans ? (
              <NavLink to="/admin/free-plans">Free plans</NavLink>
            ) : null}
            {staffAccess.canEditPlans ? <NavLink to="/admin/plans">Plans</NavLink> : null}
            <NavLink to="/admin/moderation">Moderation</NavLink>
            <NavLink to="/admin/support">Support inbox</NavLink>
            {session.user.platformRole === 'SUPER_ADMIN' ? (
              <>
                <NavLink to="/admin/activity">Activity</NavLink>
                <NavLink to="/admin/staff">Staff</NavLink>
              </>
            ) : null}
          </nav>
        ) : null}
      </aside>
      <div className={s.main}>
        <header className={s.top}>
          <div className={ui.row}>
            <Button
              variant="secondary"
              size="sm"
              className={s.menuBtn}
              onClick={() => {
                setMenuOpenOn(menuOpenOn === pathname ? null : pathname);
              }}
              aria-expanded={menuOpenOn === pathname}
            >
              Menu
            </Button>
            <label htmlFor="business-switcher" className="sr-only">
              Business
            </label>
            <select
              id="business-switcher"
              className={`${ui.input} ${s.switcher}`}
              value={selectedId ?? ''}
              onChange={(e) => {
                if (e.target.value === '__new') void navigate('/onboarding');
                else select(e.target.value);
              }}
            >
              {businesses.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                  {b.status === 'SUSPENDED' ? ' (suspended)' : ''}
                </option>
              ))}
              <option value="__new">+ Add a business</option>
            </select>
          </div>
          <div className={ui.row}>
            <Link
              to="/notifications"
              className={s.bell}
              aria-label={`Notifications${unread.data?.count ? `, ${unread.data.count} unread` : ''}`}
            >
              Notifications
              {unread.data?.count ? <span className={s.count}>{unread.data.count}</span> : null}
            </Link>
            <Link to="/account" className={s.user} title="My account">
              <span className={s.avatar} aria-hidden="true">
                {initials(session?.user.displayName ?? '')}
              </span>
              <span className={s.userName}>{session?.user.displayName}</span>
            </Link>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void signOut().then(() => navigate('/login'))}
            >
              Log out
            </Button>
          </div>
        </header>
        <main className={s.content} id="main">
          {context && !context.business.isClaimed ? (
            <p className={ui.banner}>
              This business is not verified yet, so you can&apos;t reply publicly.{' '}
              <Link to="/settings#claim">Verify ownership</Link>
            </p>
          ) : null}
          <EnforcementGuard>
            <Outlet />
          </EnforcementGuard>
        </main>
      </div>
    </div>
  );
}
